import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, apiFetch } from "@/lib/api";
import type { Page, ReferenceListItemResource, VendorResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";
import { formatMobilePhoneInput } from "@/lib/phone-format";

/**
 * The "Add vendor" creation flow (Story 16.2) — mirrors `AddTenantDialog.
 * tsx`'s own established "plain one-step form" shape (spec's own bullet 3),
 * captures `full_name`/`mobile_phone`/`trade` (the only three `POST /vendors`
 * actually requires, `app/api/vendors.py::CreateVendorRequest`) plus every
 * other field the AC names. No lease-details section — `AddTenantDialog.
 * tsx`'s own optional "Lease details" block (Story 15.9) is a `Tenant`
 * -specific concern with no `Vendor` analogue (a vendor has no lease), so
 * this dialog is genuinely simpler, not a stripped-down copy with dead code
 * left behind.
 *
 * `trade` (VC-16.2-07) is a real `<select>` sourced from `GET
 * /reference-list-items?list_key=vendor_trade` — mirrors `AddDocumentDialog.
 * tsx`'s own identical `category` `<select>` pattern exactly, parameterized
 * to `vendor_trade` instead of `document_category`. Unlike `category`
 * (optional, "Uncategorised" default), `trade` is required — `CreateVendorRequest.
 * trade` has no default and a blank string is refused with a `422`
 * (`app/api/vendors.py`'s own `_not_blank` validator) — so this dialog
 * blocks submission client-side rather than letting that reach the network,
 * mirroring `AddTenantDialog.tsx`'s own `canSubmit` client-side gate for its
 * two required fields.
 */
const ADD_VENDOR_ERROR_ID = "add-vendor-error";

export function AddVendorDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [fullName, setFullName] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [trade, setTrade] = useState("");
  const [email, setEmail] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  // Live-caught: a rejected submission only ever surfaced one generic
  // message, with no indication of which field caused it — even though
  // `app/api/errors.py::_validation_exception_handler` already extracts and
  // returns the real field name (`ApiError.field`) for exactly this case.
  const [errorField, setErrorField] = useState<string | undefined>(undefined);
  const HIDDEN_FIELDS = new Set(["email", "preferred_contact_method", "company_name", "notes"]);
  // Mirrors RecordPage.tsx's own "More details" progressive-disclosure
  // pattern (same button text, same aria-expanded wiring) — live-caught:
  // this dialog previously surfaced all 7 fields flat, at once, unlike the
  // vendor record page one screen away, which already defers everything but
  // the primary fields.
  const [moreOpen, setMoreOpen] = useState(false);

  const tradesQuery = useQuery({
    queryKey: ["reference-list-items", "vendor_trade"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=vendor_trade"),
    enabled: open,
  });
  const tradeOptions = tradesQuery.data?.data ?? [];

  useEffect(() => {
    if (!open) {
      setFullName("");
      setMobilePhone("");
      setTrade("");
      setEmail("");
      setPreferredContactMethod("");
      setCompanyName("");
      setNotes("");
      setError(undefined);
      setErrorField(undefined);
      setMoreOpen(false);
    }
  }, [open]);

  const canSubmit = fullName.trim() !== "" && mobilePhone.trim() !== "" && trade !== "";

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    setErrorField(undefined);
    try {
      const created = await apiFetch<VendorResource>("/vendors", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fullName.trim(),
          mobile_phone: mobilePhone.trim(),
          trade,
          email: email.trim() === "" ? null : email.trim(),
          preferred_contact_method: preferredContactMethod === "" ? null : preferredContactMethod,
          company_name: companyName.trim() === "" ? null : companyName.trim(),
          notes: notes.trim() === "" ? null : notes.trim(),
        }),
      });
      setIsSubmitting(false);
      onOpenChange(false);
      onCreated(created.id);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
      if (submitError instanceof ApiError && submitError.field) {
        setErrorField(submitError.field);
        if (HIDDEN_FIELDS.has(submitError.field)) {
          setMoreOpen(true);
        }
      }
      setIsSubmitting(false);
    }
  }

  const CONTACT_METHOD_OPTIONS = [
    { value: "", label: "Unspecified" },
    { value: "phone", label: "Phone" },
    { value: "email", label: "Email" },
    { value: "text", label: "Text" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add a vendor">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("full_name")} <span className="font-normal">(required)</span>
          </span>
          <Input
            autoFocus
            required
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            invalid={errorField === "full_name"}
            aria-describedby={errorField === "full_name" ? ADD_VENDOR_ERROR_ID : undefined}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("mobile_phone")} <span className="font-normal">(required)</span>
          </span>
          <Input
            required
            type="tel"
            value={mobilePhone}
            onChange={(event) => setMobilePhone(formatMobilePhoneInput(event.target.value))}
            invalid={errorField === "mobile_phone"}
            aria-describedby={errorField === "mobile_phone" ? ADD_VENDOR_ERROR_ID : undefined}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">
            {labelFor("trade")} <span className="font-normal">(required)</span>
          </span>
          <Select
            required
            value={trade}
            onChange={(event) => setTrade(event.target.value)}
            invalid={errorField === "trade"}
            aria-describedby={errorField === "trade" ? ADD_VENDOR_ERROR_ID : undefined}
          >
            <option value="">Select a trade</option>
            {tradeOptions.map((option) => (
              <option key={option.id} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        <div>
          <button
            type="button"
            onClick={() => setMoreOpen((next) => !next)}
            aria-expanded={moreOpen}
            className="inline-flex h-tap-target-min items-center text-body font-medium text-accent underline"
          >
            {moreOpen ? "Hide details" : "More details"}
          </button>
        </div>

        {moreOpen ? (
          <>
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("company_name")}</span>
              <Input
                value={companyName}
                onChange={(event) => setCompanyName(event.target.value)}
                invalid={errorField === "company_name"}
                aria-describedby={errorField === "company_name" ? ADD_VENDOR_ERROR_ID : undefined}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("email")}</span>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                invalid={errorField === "email"}
                aria-describedby={errorField === "email" ? ADD_VENDOR_ERROR_ID : undefined}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">
                {labelFor("preferred_contact_method")}
              </span>
              <Select
                value={preferredContactMethod}
                onChange={(event) => setPreferredContactMethod(event.target.value)}
                invalid={errorField === "preferred_contact_method"}
                aria-describedby={errorField === "preferred_contact_method" ? ADD_VENDOR_ERROR_ID : undefined}
              >
                {CONTACT_METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("notes")}</span>
              <Input
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                invalid={errorField === "notes"}
                aria-describedby={errorField === "notes" ? ADD_VENDOR_ERROR_ID : undefined}
              />
            </label>
          </>
        ) : null}

        {error ? (
          <p id={ADD_VENDOR_ERROR_ID} role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Adding…" : "Add vendor"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
